import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, ArrowRight, Loader2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { buildPayload } from "@/lib/forms";
import { useSubmit } from "@/hooks/useSubmit";
import { eyebrowFor } from "@/content/sections";
import { reveal } from "@/lib/motion";

const inputCls = "bg-white/5 border-white/15 rounded-none text-white placeholder:text-zinc-500 focus-visible:ring-1 focus-visible:ring-neon focus-visible:ring-offset-0";
const labelCls = "font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400";

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className={labelCls}>{label}</Label>
    {children}
  </div>
);

const SubmitBtn = ({ loading, children, testid }) => (
  <button
    type="submit"
    disabled={loading}
    data-testid={testid}
    className="group mt-2 flex items-center justify-center gap-2 bg-neon px-8 py-4 font-mono text-sm font-semibold uppercase tracking-[0.15em] text-black transition-colors duration-300 hover:bg-white disabled:opacity-60"
  >
    {loading ? <Loader2 size={16} className="animate-spin" /> : children}
    {!loading && <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
  </button>
);

const QuoteForm = () => {
  const { loading, submit } = useSubmit("quote");
  const [date, setDate] = useState(null);
  const [service, setService] = useState("");

  const onSubmit = (e) => {
    e.preventDefault();
    if (!service) { toast.error("Please select a service."); return; }
    const payload = buildPayload("quote", e.target, {
      service, pickup_date: date ? format(date, "yyyy-MM-dd") : "",
    });
    submit(payload, () => { e.target.reset(); setDate(null); setService(""); });
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="quote-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Company"><Input name="company" required className={inputCls} data-testid="quote-company" /></Field>
        <Field label="Contact Name"><Input name="contact_name" required className={inputCls} data-testid="quote-contact" /></Field>
        <Field label="Email"><Input name="email" type="email" required className={inputCls} data-testid="quote-email" /></Field>
        <Field label="Phone"><Input name="phone" className={inputCls} data-testid="quote-phone" /></Field>
        <Field label="Service">
          <Select value={service} onValueChange={setService}>
            <SelectTrigger className={inputCls} data-testid="quote-service"><SelectValue placeholder="Select service" /></SelectTrigger>
            <SelectContent className="rounded-none border-white/15 bg-[#0a0a0a] text-white">
              {["Full Truckload (FTL)", "Less-Than-Truckload (LTL)", "Intermodal", "Refrigerated (Reefer)", "Expedited", "Warehousing / 3PL"].map((s) => (
                <SelectItem key={s} value={s} className="focus:bg-neon focus:text-black">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Pickup Date">
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" data-testid="quote-date" className={`flex h-10 w-full items-center gap-2 border px-3 text-left text-sm ${inputCls} ${date ? "text-white" : "text-zinc-500"}`}>
                <CalendarIcon size={15} className="text-neon" />
                {date ? format(date, "PPP") : "Pick a date"}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto rounded-none border-white/15 bg-[#0a0a0a] p-0" align="start">
              <Calendar mode="single" selected={date} onSelect={setDate} autoFocus className="text-white" />
            </PopoverContent>
          </Popover>
        </Field>
        <Field label="Origin"><Input name="origin" required placeholder="City, ST" className={inputCls} data-testid="quote-origin" /></Field>
        <Field label="Destination"><Input name="destination" required placeholder="City, ST" className={inputCls} data-testid="quote-destination" /></Field>
        <Field label="Freight Type"><Input name="freight_type" placeholder="e.g. Palletized" className={inputCls} /></Field>
        <Field label="Weight"><Input name="weight" placeholder="e.g. 12,000 lbs" className={inputCls} /></Field>
      </div>
      <Field label="Notes"><Textarea name="notes" rows={3} className={inputCls} /></Field>
      <SubmitBtn loading={loading} testid="quote-submit-button">Request Quote</SubmitBtn>
    </form>
  );
};

const CarrierForm = () => {
  const { loading, submit } = useSubmit("carrier");

  const onSubmit = (e) => {
    e.preventDefault();
    submit(buildPayload("carrier", e.target), () => e.target.reset());
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="carrier-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Company"><Input name="company" required className={inputCls} data-testid="carrier-company" /></Field>
        <Field label="Contact Name"><Input name="contact_name" required className={inputCls} data-testid="carrier-contact" /></Field>
        <Field label="Email"><Input name="email" type="email" required className={inputCls} data-testid="carrier-email" /></Field>
        <Field label="Phone"><Input name="phone" required className={inputCls} data-testid="carrier-phone" /></Field>
        <Field label="MC Number"><Input name="mc_number" className={inputCls} /></Field>
        <Field label="DOT Number"><Input name="dot_number" className={inputCls} /></Field>
        <Field label="Equipment"><Input name="equipment" placeholder="Reefer, Dry Van..." className={inputCls} /></Field>
        <Field label="Fleet Size"><Input name="fleet_size" placeholder="e.g. 12 trucks" className={inputCls} /></Field>
      </div>
      <Field label="Preferred Lanes"><Textarea name="lanes" rows={3} placeholder="e.g. TX → CA, Midwest regional" className={inputCls} /></Field>
      <SubmitBtn loading={loading} testid="carrier-submit-button">Join the Network</SubmitBtn>
    </form>
  );
};

const ContactForm = () => {
  const { loading, submit } = useSubmit("contact");

  const onSubmit = (e) => {
    e.preventDefault();
    submit(buildPayload("contact", e.target), () => e.target.reset());
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Name"><Input name="name" required className={inputCls} data-testid="contact-name" /></Field>
        <Field label="Email"><Input name="email" type="email" required className={inputCls} data-testid="contact-email" /></Field>
      </div>
      <Field label="Company"><Input name="company" className={inputCls} /></Field>
      <Field label="Message"><Textarea name="message" rows={5} required className={inputCls} data-testid="contact-message" /></Field>
      <SubmitBtn loading={loading} testid="contact-submit-button">Send Message</SubmitBtn>
    </form>
  );
};

export const Connect = ({ tab, setTab }) => {
  return (
    <section id="connect" className="relative z-10 bg-[#0a0a0a] py-28 lg:py-40" data-testid="connect">
      <div className="mx-auto max-w-[1500px] px-6 lg:px-12">
        <div className="grid gap-16 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-neon">{eyebrowFor("connect")}</span>
            <h2 className="mt-4 font-display text-4xl font-bold uppercase leading-none tracking-tight text-white md:text-6xl">
              Let&apos;s move<br />something.
            </h2>
            <p className="mt-8 max-w-md leading-relaxed text-zinc-400">
              Shippers get a rate. Carriers get loads. Everyone gets a human who answers.
              Fill the form and our dispatch desk responds fast.
            </p>
            <div className="mt-10 space-y-3 font-mono text-sm text-zinc-300">
              <p><span className="text-neon">DISPATCH</span>  +1 (469) 450-0092</p>
              <p><span className="text-neon">EMAIL</span>  ops@apexlinklogistics.net</p>
              <p><span className="text-neon">HOURS</span>  24 / 7 / 365</p>
            </div>
          </div>

          <motion.div
            {...reveal}
            className="tracing-card border border-white/10 bg-ink p-6 md:p-10"
          >
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="mb-8 grid w-full grid-cols-3 rounded-none border border-white/10 bg-transparent p-0">
                {[["quote", "Get a Quote"], ["carrier", "Carriers"], ["contact", "Contact"]].map(([v, l]) => (
                  <TabsTrigger
                    key={v}
                    value={v}
                    data-testid={`tab-${v}`}
                    className="rounded-none py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400 data-[state=active]:bg-neon data-[state=active]:text-black"
                  >
                    {l}
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value="quote" forceMount className="data-[state=inactive]:hidden">
                <QuoteForm />
              </TabsContent>

              <TabsContent value="carrier" forceMount className="data-[state=inactive]:hidden">
                <CarrierForm />
              </TabsContent>

              <TabsContent value="contact" forceMount className="data-[state=inactive]:hidden">
                <ContactForm />
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
